/**
 * DiscoveryService (AJIP-16.02.005 · WS6)
 * ---------------------------------------------------------------------
 * Deterministic. Configuration-driven. NEVER activates a journey.
 * NEVER renders UI. NEVER modifies journey state.
 *
 * Per Additional Founder Directive: Discovery Service shall remain
 * deterministic. Future AI capabilities may recommend; they shall
 * never determine.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { RegistrationService } from './RegistrationService';
import { freezeContext } from './DiscoveryContext';
import { evaluateEligibility, DECISIONS } from './EligibilityEvaluator';
import { orderJourneys } from './DiscoveryOrdering';
import { REGISTRATION_DISCOVERY_EVENTS } from './RegistrationDiscoveryObservability';

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. DiscoveryService is inert.');
};

class DiscoveryService {
  constructor({ registrationService, nowFn = () => Date.now() } = {}) {
    if (!(registrationService instanceof RegistrationService)) {
      throw new Error('DiscoveryService requires a RegistrationService');
    }
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    this._reg = registrationService;
    this._observability = registrationService.observability;
    this._now = nowFn;
  }

  get enabled() { return this._enabled; }

  _all() {
    if (!this._enabled) return Object.freeze([]);
    return this._reg.listRegisteredJourneys();
  }

  _emit(event, payload) { this._observability.emit(event, payload); }

  discoverAll(ctx = null) {
    if (!this._enabled) return Object.freeze([]);
    freezeContext(ctx);
    this._emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_DISCOVERY_REQUESTED, { kind: 'all' });
    const out = orderJourneys(this._all());
    if (out.length === 0) this._emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_DISCOVERY_RETURNED_EMPTY, { kind: 'all' });
    else this._emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_DISCOVERY_COMPLETED, { kind: 'all', count: out.length });
    return out;
  }

  discoverByType(journeyType) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.journeyType === journeyType));
  }

  discoverByCategory(category) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.journeyCategory === category));
  }

  discoverByAudience(audience) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.journeyAudience.includes(audience)));
  }

  discoverByTag(tag) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => (d.tags || []).includes(tag)));
  }

  discoverByAvailability(status) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.registrationStatus === status));
  }

  discoverByFeatureFlags(activeFlags) {
    if (!this._enabled) return Object.freeze([]);
    const set = new Set(activeFlags || []);
    return orderJourneys(this._all().filter((d) => d.requiredFeatureFlags.every((f) => set.has(f))));
  }

  discoverByPermissions(permissions) {
    if (!this._enabled) return Object.freeze([]);
    const set = new Set(permissions || []);
    return orderJourneys(this._all().filter((d) => d.permissionRequirements.every((p) => set.has(p))));
  }

  discoverByPersistenceCapability(required) {
    if (!this._enabled) return Object.freeze([]);
    if (required) {
      return orderJourneys(this._all().filter((d) => d.persistencePolicyRef !== null));
    }
    return orderJourneys(this._all().filter((d) => d.persistencePolicyRef === null));
  }

  discoverByLifecycleStatus(status) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.deprecationStatus === status));
  }

  discoverByPriority(min, max) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => {
      const p = d.priority;
      if (typeof min === 'number' && p < min) return false;
      if (typeof max === 'number' && p > max) return false;
      return true;
    }));
  }

  discoverByCanonReference(canonRef) {
    if (!this._enabled) return Object.freeze([]);
    return orderJourneys(this._all().filter((d) => d.canonReference === canonRef));
  }

  getById(journeyId) {
    _requireEnabled(this);
    return this._reg.listRegisteredJourneys().find((d) => d.journeyId === journeyId) || null;
  }

  getByVersion(journeyId, version) {
    _requireEnabled(this);
    const d = this.getById(journeyId);
    if (!d) return null;
    return d.journeyVersion === version ? d : null;
  }

  discoverEligibleFor(ctx) {
    if (!this._enabled) return Object.freeze([]);
    freezeContext(ctx);
    const ordered = orderJourneys(this._all());
    const eligible = [];
    for (const def of ordered) {
      const decision = evaluateEligibility({ definition: def, context: ctx, nowFn: this._now });
      this._emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_ELIGIBILITY_EVALUATED, {
        journeyId: def.journeyId, decision: decision.decision, reason: decision.reason,
      });
      if (decision.decision === DECISIONS.ELIGIBLE) {
        eligible.push(Object.freeze({ definition: def, decision }));
      } else {
        this._emit(REGISTRATION_DISCOVERY_EVENTS.JOURNEY_ELIGIBILITY_REJECTED, {
          journeyId: def.journeyId, decision: decision.decision, reason: decision.reason,
        });
      }
    }
    return Object.freeze(eligible);
  }
}

export { DiscoveryService };
